import type { Perfil, Ref } from "@vetbolso/contracts";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { sair, salvarPerfil } from "./api.ts";
import { apagarBase, type Base, type EstadoBase, sincronizar } from "./base.ts";

export const guardado = {
  ler<T>(k: string, padrao: T): T {
    try {
      const v = localStorage.getItem(`vetbolso.${k}`);
      return v == null ? padrao : (JSON.parse(v) as T);
    } catch {
      return padrao;
    }
  },
  gravar(k: string, v: unknown) {
    try {
      localStorage.setItem(`vetbolso.${k}`, JSON.stringify(v));
    } catch {
      /* sem armazenamento */
    }
  },
};

/** Folha (bottom sheet) aberta. Fonte abre por cima de qualquer folha; dose e produto, por cima da bula. */
export type Folha =
  | { t: "bula" | "dose" | "tox" | "prod" | "regra" | "planta"; id: string }
  | { t: "ref"; f: string; loc?: string; cit?: string; nv?: boolean };

export type ItemReceita = { farmaco: string; pos: string; controle?: string; refs?: Ref[] };
/** Unidade em que o peso é digitado e mostrado; o cálculo usa sempre kg. */
export type UnPeso = "kg" | "g";
export type Rascunho = { animal: string; tutor: string; cpf: string; peso: string; pesoUn?: UnPeso; itens: ItemReceita[] };

type Ui = {
  especie: string;
  setEspecie: (e: string) => void;
  perfil: Perfil;
  setPerfil: (p: Perfil) => void;
  peso: number;
  setPeso: (p: number) => void;
  unPeso: UnPeso;
  setUnPeso: (u: UnPeso) => void;
  folhas: Folha[];
  abrir: (f: Folha) => void;
  fechar: () => void;
  fecharTodas: () => void;
  rx: Rascunho;
  setRx: (f: (r: Rascunho) => Rascunho) => void;
  toxCat: string;
  setToxCat: (c: string) => void;
  calc: string;
  setCalc: (c: string) => void;
  q: string;
  setQ: (q: string) => void;
};

type Ctx = Ui & {
  estado: EstadoBase;
  recarregar: () => Promise<void>;
  sairDaConta: () => Promise<void>;
  sincronizando: boolean;
};
const Contexto = createContext<Ctx | null>(null);

/** Sincroniza ao abrir, ao voltar a rede, ao voltar para o app e a cada 30 min. */
export function ProvedorApp({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoBase>({ fase: "carregando" });
  const [sincronizando, setSincronizando] = useState(false);
  const ultima = useRef(0);
  const emCurso = useRef<Promise<void> | null>(null);

  const recarregar = useCallback(() => {
    if (emCurso.current) return emCurso.current;
    setSincronizando(true);
    const p = sincronizar()
      .then(setEstado)
      .catch((e: unknown) => setEstado({ fase: "erro", msg: e instanceof Error ? e.message : "falha ao carregar a base" }))
      .finally(() => {
        ultima.current = Date.now();
        emCurso.current = null;
        setSincronizando(false);
      });
    emCurso.current = p;
    return p;
  }, []);

  useEffect(() => {
    recarregar();
    const naRede = () => recarregar();
    const aoVoltar = () => document.visibilityState === "visible" && Date.now() - ultima.current > 5 * 60_000 && recarregar();
    const t = setInterval(() => recarregar(), 30 * 60_000);
    window.addEventListener("online", naRede);
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", naRede);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [recarregar]);

  const [especie, setEspecieS] = useState(() => guardado.ler("especie", "cao"));
  const [perfilLocal, setPerfilS] = useState<Perfil>(() => guardado.ler("perfil", "vet"));
  const [peso, setPeso] = useState(10);
  const [unPeso, setUnPesoS] = useState<UnPeso>(() => guardado.ler("unPeso", "kg"));
  const [folhas, setFolhas] = useState<Folha[]>([]);
  const [rx, setRxS] = useState<Rascunho>({ animal: "", tutor: "", cpf: "", peso: "", itens: [] });
  const [toxCat, setToxCat] = useState("todos");
  const [calc, setCalc] = useState("fluidos");
  const [q, setQ] = useState("");

  // O perfil salvo na conta vale em todos os aparelhos; a troca local vai para o servidor quando há rede.
  const sessaoPerfil = estado.fase === "pronta" ? estado.sessao?.usuario.perfil : null;
  useEffect(() => {
    if (sessaoPerfil) {
      setPerfilS(sessaoPerfil);
      guardado.gravar("perfil", sessaoPerfil);
    }
  }, [sessaoPerfil]);
  const perfil = perfilLocal;
  const online = estado.fase === "pronta" && estado.online;

  const valor = useMemo<Ctx>(
    () => ({
      estado,
      recarregar,
      sincronizando,
      async sairDaConta() {
        await sair().catch(() => {});
        await apagarBase();
        setRxS({ animal: "", tutor: "", cpf: "", peso: "", itens: [] });
        setEstado({ fase: "entrar" });
      },
      especie,
      setEspecie(e) {
        setEspecieS(e);
        guardado.gravar("especie", e);
        setFolhas([]);
      },
      perfil,
      setPerfil(p) {
        setPerfilS(p);
        guardado.gravar("perfil", p);
        if (online)
          salvarPerfil({ perfil: p })
            .then((sessao) => setEstado((e) => (e.fase === "pronta" ? { ...e, sessao } : e)))
            .catch(() => {});
      },
      peso,
      setPeso,
      unPeso,
      setUnPeso(u) {
        setUnPesoS(u);
        guardado.gravar("unPeso", u);
      },
      folhas,
      // Uma folha principal e, no máximo, a de fonte por cima.
      abrir: (f) => setFolhas((fs) => empilhar(fs, f)),
      fechar: () => setFolhas((fs) => fs.slice(0, -1)),
      fecharTodas: () => setFolhas([]),
      rx,
      setRx: (f) => setRxS(f),
      toxCat,
      setToxCat,
      calc,
      setCalc,
      q,
      setQ,
    }),
    [estado, recarregar, sincronizando, especie, perfil, online, peso, unPeso, folhas, rx, toxCat, calc, q],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useApp() {
  const c = useContext(Contexto);
  if (!c) throw new Error("useApp fora do ProvedorApp");
  return c;
}

/** Só nas telas internas, onde a base já está carregada. */
export function useBase(): Base {
  const { estado } = useApp();
  if (estado.fase !== "pronta") throw new Error("base indisponível");
  return estado.base;
}

/** Pilha de folhas: no máximo bula → dose → fonte. O resto substitui a pilha. */
function empilhar(fs: Folha[], f: Folha): Folha[] {
  const topo = fs.at(-1);
  if (f.t === "ref") return topo ? [...fs.filter((x) => x.t !== "ref"), f] : [f];
  if ((f.t === "dose" || f.t === "prod") && topo?.t === "bula") return [topo, f];
  return [f];
}
